import React, { useState, useEffect } from "react";
import { NavLink, useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { api } from "../lib/apiClient";
import { ROLE_LABELS } from "../lib/constants";
import { Avatar } from "./Badges";
import ThemeToggle from "./ThemeToggle";
import NotificationBell from "./NotificationBell";
import { LayoutDashboard, KanbanSquare, Rocket, ShieldAlert, Settings, LogOut, ListChecks, Users, Briefcase, FileBarChart, Network, ClipboardList, Send } from "lucide-react";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger, DropdownMenuSeparator, DropdownMenuLabel,
} from "./ui/dropdown-menu";

const NAV = [
  { to: "/", label: "Dashboard", icon: LayoutDashboard, end: true, mod: "dashboard" },
  { to: "/mi-trabajo", label: "Mi trabajo", icon: Briefcase },
  { to: "/desarrollos", label: "Desarrollos", icon: KanbanSquare, mod: "desarrollos" },
  { to: "/requerimientos", label: "Consulta de Requerimientos", icon: ClipboardList },
  { to: "/solicitudes", label: "Solicitudes", icon: Send, roles: ["pm", "stakeholder", "qa"] },
  { to: "/tareas", label: "Tareas", icon: ListChecks, mod: "tareas" },
  { to: "/deploys", label: "Próximos Deploys", icon: Rocket, mod: "deploys" },
  { to: "/riesgos", label: "Riesgos", icon: ShieldAlert, mod: "riesgos" },
  { to: "/reportes", label: "Reportes", icon: FileBarChart, pmOnly: true, mod: "reportes" },
  { to: "/organigrama", label: "Organigrama", icon: Network, mod: "organigrama" },
  { to: "/usuarios", label: "Usuarios", icon: Users, pmOnly: true, mod: "usuarios" },
  { to: "/config", label: "Configuración", icon: Settings, pmOnly: true, mod: "configuracion" },
];

export default function Layout({ children }) {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [pendingSol, setPendingSol] = useState(0);

  useEffect(() => {
    if (user?.role !== "pm") return;
    let active = true;
    const fetchCount = () => api.get("/solicitudes/pending-count").then((r) => { if (active) setPendingSol(r.data.count || 0); }).catch(() => {});
    fetchCount();
    const id = setInterval(fetchCount, 30000);
    return () => { active = false; clearInterval(id); };
  }, [user, location.pathname]);

  const handleLogout = () => {
    logout();
    navigate("/login");
  };

  return (
    <div className="min-h-screen flex bg-[#FAFAFA]">
      {/* Sidebar */}
      <aside className="w-60 shrink-0 border-r border-border bg-white flex flex-col fixed h-screen">
        <div className="px-6 py-6 border-b border-border">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <div className="h-8 w-8 bg-slate-900 flex items-center justify-center rounded-sm">
                <img src="/zae-logo.png" alt="ZAE" className="h-6 w-6 object-contain" />
              </div>
              <div>
                <div className="font-heading font-extrabold text-lg leading-none tracking-tight">Sistemas</div>
                <div className="text-[10px] uppercase tracking-[0.2em] text-slate-400 mt-0.5">Gestión de Proyectos</div>
              </div>
            </div>
            <NotificationBell />
          </div>
        </div>
        <nav className="flex-1 py-4">
          {NAV.filter((item) => (item.roles ? item.roles.includes(user?.role) : (user?.role === "pm" || (!item.pmOnly && (!item.mod || !user?.modules || user.modules.includes(item.mod)))))).map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              end={item.end}
              data-testid={`nav-${item.label.toLowerCase().replace(/\s/g, "-")}`}
              className={({ isActive }) =>
                `flex items-center gap-3 px-6 py-2.5 text-sm font-medium border-l-2 transition-colors duration-200 ${
                  isActive
                    ? "border-slate-900 bg-slate-50 text-slate-900"
                    : "border-transparent text-slate-500 hover:text-slate-900 hover:bg-slate-50"
                }`
              }
            >
              <item.icon className="h-4 w-4" />
              <span className="flex-1">{item.label}</span>
              {item.to === "/solicitudes" && pendingSol > 0 && (
                <span className="ml-auto inline-flex items-center justify-center min-w-[18px] h-[18px] px-1 rounded-full bg-red-600 text-white text-[10px] font-bold" data-testid="nav-solicitudes-badge">{pendingSol}</span>
              )}
            </NavLink>
          ))}
        </nav>
        <ThemeToggle />
        <div className="border-t border-border p-4">
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button data-testid="user-menu-trigger" className="flex items-center gap-3 w-full text-left hover:bg-slate-50 p-2 rounded-sm transition-colors duration-200">
                <Avatar user={user} size={36} />
                <div className="min-w-0 flex-1">
                  <div className="text-sm font-semibold truncate">{user?.name}</div>
                  <div className="text-[11px] text-slate-400">{ROLE_LABELS[user?.role]}</div>
                </div>
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="start" className="w-52 rounded-sm">
              <DropdownMenuLabel className="text-xs">{user?.email}</DropdownMenuLabel>
              <DropdownMenuSeparator />
              <DropdownMenuItem data-testid="logout-btn" onClick={handleLogout} className="text-red-600 cursor-pointer">
                <LogOut className="h-4 w-4 mr-2" /> Cerrar sesión
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </aside>

      {/* Content */}
      <main className="flex-1 ml-60 min-w-0">{children}</main>
    </div>
  );
}
